import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PlusIcon, EyeIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import api from '../../utils/api';
import DataTable from '../../components/common/DataTable';

const ClientsList = () => {
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(location.state?.message || null);
  // eslint-disable-next-line no-unused-vars
  const [notificationType, setNotificationType] = useState(location.state?.type || 'success');

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        const response = await api.clients.list();
        const clientsData = response?.data?.results || response?.data?.data || response?.data || [];
        setClients(Array.isArray(clientsData) ? clientsData : []);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to fetch clients.');
        console.error('Failed to fetch clients:', err);
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
    
    if (location.state?.message) {
      window.history.replaceState({}, document.title);
    }
    
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification, location.state]);

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (row) => (
        <span className="font-medium text-gray-900 dark:text-white">
          {row.first_name} {row.last_name}
        </span>
      ),
      sortValue: (row) => `${row.first_name} ${row.last_name}`,
    },
    {
      key: 'email',
      label: 'Email',
    },
    {
      key: 'phone',
      label: 'Phone',
      render: (row) => row.phone || 'N/A',
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      className: 'text-right',
      render: (row) => (
        <Link
          to={`/clients/${row.id}`}
          className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-200 inline-flex items-center"
        >
          <EyeIcon className="h-5 w-5 mr-1" /> View
        </Link>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-red-500 text-lg">Error: {error}</p>
        <p className="text-gray-600 dark:text-gray-400">Could not load client data. Please try again later.</p>
      </div>
    );
  }

  return (
    <div>
      {notification && (
        <div className={`mb-4 flex items-center justify-between p-4 rounded-md shadow-sm ${
          notificationType === 'success' 
            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' 
            : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
        }`}>
          <div className="flex items-center">
            {notificationType === 'success' ? (
              <CheckCircleIcon className="h-5 w-5 mr-2" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            )}
            <span>{notification}</span>
          </div>
          <button 
            onClick={() => setNotification(null)}
            className={notificationType === 'success' ? 
              "text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100" :
              "text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100"
            }
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}
      
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Clients List</h2>
        <Link
          to="/clients/new"
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md shadow-sm hover:bg-primary-700 focus:outline-none"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add New Client
        </Link>
      </div>

      {clients.length === 0 && !loading ? (
        <div className="text-center py-10 bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/10 rounded-lg">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No clients</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new client.</p>
          <div className="mt-6">
            <Link
              to="/clients/new"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              New Client
            </Link>
          </div>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={clients}
          searchPlaceholder="Search clients by name, email, or phone…"
          searchKeys={['first_name', 'last_name', 'email', 'phone']}
          emptyMessage="No clients found."
          defaultSort={{ key: 'name', direction: 'asc' }}
        />
      )}
    </div>
  );
};

export default ClientsList;
